// Admin Dashboard page
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heading, Button } from "../atoms";
import { AdminLayout } from "../organisms";
import { donationService } from "../../services/donationService";
import { jobService } from "../../services/jobService";
import { useAuth } from "../../contexts/AuthContext";

export const AdminDashboard = () => {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState({
    totalDonations: 0,
    donationCount: 0,
    jobsCount: 0,
    jobsByStatus: { started: 0, in_progress: 0, completed: 0, cancelled: 0 },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [donationsSummary, donations, jobs] = await Promise.all([
        donationService.getDonationsSummary(),
        donationService.getDonations(),
        jobService.getJobs(),
      ]);

      setStats({
        totalDonations: donationsSummary.totalAmount || 0,
        donationCount: donations.length || 0,
        jobsCount: jobs.length || 0,
        jobsByStatus: {
          started: jobs.filter((j) => j.status === "started").length,
          in_progress: jobs.filter((j) => j.status === "in_progress").length,
          completed: jobs.filter((j) => j.status === "completed").length,
          cancelled: jobs.filter((j) => j.status === "cancelled").length,
        },
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title="Admin Dashboard">
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60">
          <h3 className="text-ink-soft text-sm font-semibold uppercase mb-2">
            Total Donations
          </h3>
          <p className="font-display text-4xl font-semibold text-terracotta-700">
            {loading ? "…" : `£${stats.totalDonations.toFixed(2)}`}
          </p>
          <p className="text-ink-soft text-sm mt-2">
            {stats.donationCount} donations
          </p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60">
          <h3 className="text-ink-soft text-sm font-semibold uppercase mb-2">
            Active Jobs
          </h3>
          <p className="font-display text-4xl font-semibold text-ink-teal">
            {loading ? "…" : stats.jobsCount}
          </p>
          <p className="text-ink-soft text-sm mt-2">Job requests</p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60">
          <h3 className="text-ink-soft text-sm font-semibold uppercase mb-4">
            Jobs by Status
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-ink">
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block"></span>
                New Requests
              </span>
              <span className="text-sm font-bold text-yellow-700">
                {loading ? "…" : stats.jobsByStatus.started}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-ink">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span>
                In Progress
              </span>
              <span className="text-sm font-bold text-blue-700">
                {loading ? "…" : stats.jobsByStatus.in_progress}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-ink">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block"></span>
                Completed
              </span>
              <span className="text-sm font-bold text-green-700">
                {loading ? "…" : stats.jobsByStatus.completed}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-ink">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block"></span>
                Cancelled
              </span>
              <span className="text-sm font-bold text-red-700">
                {loading ? "…" : stats.jobsByStatus.cancelled}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60 flex flex-col">
          <Heading level={3} className="mb-4">
            Manage Jobs
          </Heading>
          <p className="text-ink-soft mb-4 flex-1">
            View and update status of job requests from widows.
          </p>
          <div className="flex justify-end">
            <Link to="/admin/jobs">
              <Button variant="primary" className="!px-3 !py-1.5 !text-sm">
                Open
              </Button>
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60 flex flex-col">
          <Heading level={3} className="mb-4">
            Manage Donations
          </Heading>
          <p className="text-ink-soft mb-4 flex-1">
            View and manage all donations received through the platform.
          </p>
          <div className="flex justify-end">
            <Link to="/admin/donations">
              <Button variant="primary" className="!px-3 !py-1.5 !text-sm">
                Open
              </Button>
            </Link>
          </div>
        </div>

        {isAdmin && (
          <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60 flex flex-col">
            <Heading level={3} className="mb-4">
              Manage Stories
            </Heading>
            <p className="text-ink-soft mb-4 flex-1">
              Create and manage stories of God's provision for the homepage.
            </p>
            <div className="flex justify-end">
              <Link to="/admin/stories">
                <Button variant="primary" className="!px-3 !py-1.5 !text-sm">
                  Open
                </Button>
              </Link>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60 flex flex-col">
            <Heading level={3} className="mb-4">
              User Management
            </Heading>
            <p className="text-ink-soft mb-4 flex-1">
              Manage admin users and their access to the platform.
            </p>
            <div className="flex justify-end">
              <Link to="/admin/users">
                <Button variant="primary" className="!px-3 !py-1.5 !text-sm">
                  Open
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
